import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from '@/hooks/useTranslation';
import {
  loadAudioDurationFromFile,
  loadMediaDimensionsFromFile,
  loadVideoDurationFromFile,
  type MediaDimensions,
} from '@/components/InfiniteCanvas/utils/mediaDimensions';
import { buildUploadSafeImage } from '@/lib/image/uploadSafeImage';
import type { ImageMediaHealth } from '@/lib/image/mediaHealth';
import { buildReportableError, reportUploadErrorToSentry } from '@/lib/sentryReporting';
import { pushDataLayerEvent } from '@/lib/gtm';

type UploadResultNodeType = 'image' | 'video' | 'audio';
type UploadTrackingNodeType = UploadResultNodeType | 'model';

export interface UploadResult {
  url: string;
  fileName: string;
  nodeType: UploadResultNodeType;
  dimensions?: MediaDimensions;
  videoDurationSec?: number;
  audioDurationSec?: number;
  aspectRatioWarning?: boolean;
  mediaHealth?: ImageMediaHealth;
}

interface PresignUploadResponse {
  success: boolean;
  uploadUrl?: string;
  url?: string;
  method?: 'PUT';
  error?: string;
}

export interface UseFileUploadOptions {
  onSuccess?: (
    url: string,
    fileName: string,
    nodeType: UploadResultNodeType,
    dimensions?: MediaDimensions,
    audioDurationSec?: number,
    videoDurationSec?: number,
    aspectRatioWarning?: boolean,
    mediaHealth?: ImageMediaHealth,
  ) => void;
  onError?: (error: string) => void;
}

interface UploadFileOptions {
  silent?: boolean;
  onProgress?: (progress: number) => void;
  assetKind?: 'media' | '3d';
}

function getUploadProvider(url: string) {
  if (url.includes('.r2.cloudflarestorage.com') || url.includes('files.musein.ai')) {
    return 'r2';
  }

  if (url.includes('.myqcloud.com') || url.includes('files.musein.cn')) {
    return 'cos';
  }

  return 'unknown';
}

function getFileSizeBucket(size: number) {
  if (size < 1024 * 1024) return 'lt_1mb';
  if (size < 10 * 1024 * 1024) return '1mb_10mb';
  if (size < 50 * 1024 * 1024) return '10mb_50mb';
  if (size < 100 * 1024 * 1024) return '50mb_100mb';
  return 'gte_100mb';
}

function buildUploadPayload(file: File, nodeType: UploadTrackingNodeType, extra: Record<string, unknown> = {}) {
  return {
    page_type: 'studio',
    section: 'asset_upload',
    file_type: file.type || 'application/octet-stream',
    file_size_bucket: getFileSizeBucket(file.size),
    node_type: nodeType,
    ...extra,
  };
}

function uploadFileWithProgress(url: string, method: 'PUT', file: File, onProgress?: (progress: number) => void) {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable) {
        return;
      }

      const nextProgress = Math.min(100, Math.max(0, Math.round((event.loaded / event.total) * 100)));
      onProgress?.(nextProgress);
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(100);
        resolve();
        return;
      }

      reject(new Error(`Upload failed with status ${xhr.status}`));
    };

    xhr.onerror = () => {
      reject(new Error('Upload failed'));
    };

    xhr.onabort = () => {
      reject(new Error('Upload aborted'));
    };

    xhr.send(file);
  });
}

function isSvgFile(file: File) {
  return file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');
}

async function hashFileContent(file: File) {
  const buffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  const truncatedBytes = new Uint8Array(digest).slice(0, 16);
  const base64 = btoa(String.fromCharCode(...truncatedBytes));
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

export function useFileUpload(options: UseFileUploadOptions = {}) {
  const [uploading, setUploading] = useState(false);
  const { t } = useTranslation();

  const uploadFile = useCallback(async (file: File, uploadOptions: UploadFileOptions = {}): Promise<UploadResult | null> => {
    const { silent = false, onProgress, assetKind = 'media' } = uploadOptions;
    let processingToastId: string | number | undefined;

    if (isSvgFile(file)) {
      const errorMessage = t('infiniteCanvas.toast.svgUploadNotSupported');
      pushDataLayerEvent('asset_upload_failed', buildUploadPayload(file, 'image', {
        failure_stage: 'validation',
        error_code: 'svg_not_supported',
        reason: errorMessage,
      }));
      if (!silent) {
        toast.error(`${t('infiniteCanvas.toast.uploadFailed')}: ${errorMessage}`, {
          duration: Infinity,
          action: {
            label: t('common.confirm'),
            onClick: () => {},
          },
        });
      }
      options.onError?.(errorMessage);
      return null;
    }

    const fileType = file.type;

    let nodeType: UploadResultNodeType = 'image';
    let trackingNodeType: UploadTrackingNodeType = 'image';
    let folder = 'images';
    if (assetKind === '3d') {
      trackingNodeType = 'model';
      folder = 'models';
    } else if (fileType.startsWith('video/')) {
      nodeType = 'video';
      trackingNodeType = 'video';
      folder = 'videos';
    } else if (fileType.startsWith('audio/')) {
      nodeType = 'audio';
      trackingNodeType = 'audio';
      folder = 'audios';
    }

    let fileToUpload = file;
    let dimensions: MediaDimensions | undefined;
    let videoDurationSec: number | undefined;
    let audioDurationSec: number | undefined;
    let contentHash = '';
    let aspectRatioWarning = false;
    let mediaHealth: ImageMediaHealth | undefined;

    try {
      if (nodeType === 'image') {
        try {
          const optimizedImage = await buildUploadSafeImage(file, {
            onTransformStart: () => {
              if (silent || processingToastId != null) {
                return;
              }

              processingToastId = toast.loading(t('infiniteCanvas.toast.optimizeOversizedImage'));
            },
          });
          fileToUpload = optimizedImage.file;
          dimensions = optimizedImage.dimensions;
          aspectRatioWarning = optimizedImage.aspectRatioWarning;
          mediaHealth = optimizedImage.mediaHealth;
        } catch (error) {
          // Fall back to uploading the original file when optimization fails.
          // The reference image compliance check on the node will surface a
          // repair hint instead of blocking the upload entirely.
          reportUploadErrorToSentry(
            error instanceof Error
              ? error
              : buildReportableError('Image optimization failed', { cause: error }),
            {
              runtime: 'browser',
              stage: 'prepare',
              fileName: file.name,
              fileType: file.type || 'application/octet-stream',
              fileSize: file.size,
              nodeType: trackingNodeType,
              folder,
            }
          );
          pushDataLayerEvent('asset_upload_failed', buildUploadPayload(file, trackingNodeType, {
            failure_stage: 'prepare',
            error_code: 'image_optimize_failed',
            reason: error instanceof Error ? error.message : 'Image optimization failed',
          }));
          fileToUpload = file;
        }
      }

      dimensions = dimensions ?? (
        nodeType === 'image' || nodeType === 'video'
          ? await loadMediaDimensionsFromFile(fileToUpload, nodeType)
          : undefined
      );
      videoDurationSec = nodeType === 'video'
        ? await loadVideoDurationFromFile(fileToUpload)
        : undefined;
      audioDurationSec = nodeType === 'audio'
        ? await loadAudioDurationFromFile(fileToUpload)
        : undefined;
      contentHash = await hashFileContent(fileToUpload);
    } catch (error) {
      const reportableError = error instanceof Error
        ? error
        : buildReportableError('Upload preparation failed', { cause: error });
      reportUploadErrorToSentry(reportableError, {
        runtime: 'browser',
        stage: 'prepare',
        fileName: fileToUpload.name,
        fileType: fileToUpload.type || 'application/octet-stream',
        fileSize: fileToUpload.size,
        nodeType: trackingNodeType,
        folder,
      });
      pushDataLayerEvent('asset_upload_failed', buildUploadPayload(fileToUpload, trackingNodeType, {
        failure_stage: 'prepare',
        error_code: 'upload_prepare_failed',
        reason: reportableError.message,
      }));
      if (!silent) {
        const errorTitle = nodeType === 'image'
          ? t('infiniteCanvas.toast.optimizeOversizedImageFailed')
          : t('infiniteCanvas.toast.uploadFailed');
        toast.error(`${errorTitle}: ${reportableError.message}`, {
          ...(processingToastId != null ? { id: processingToastId } : {}),
          duration: Infinity,
          action: {
            label: t('common.confirm'),
            onClick: () => {},
          },
        });
      }
      options.onError?.(reportableError.message);
      return null;
    }

    setUploading(true);
    if (!silent && processingToastId != null) {
      toast.loading(t('infiniteCanvas.toast.uploading'), { id: processingToastId });
    }
    onProgress?.(0);
    pushDataLayerEvent('asset_upload_started', buildUploadPayload(fileToUpload, trackingNodeType, {
      upload_method: 'direct_upload',
    }));

    let failureStage: 'presign_request' | 'presign_response' | 'direct_upload' = 'presign_request';
    let uploadMethod: 'POST' | 'PUT' = 'POST';
    let uploadUrl: string | undefined;
    let uploadProvider: string | undefined;
    let uploadStatus: number | undefined;

    try {
      const presignResponse = await fetch('/node-api/upload', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileName: fileToUpload.name,
          fileType: fileToUpload.type || 'application/octet-stream',
          folder,
          contentHash,
        }),
      });

      failureStage = 'presign_response';
      uploadStatus = presignResponse.status;
      const result = await presignResponse.json() as PresignUploadResponse;

      if (!presignResponse.ok || !result.success || !result.uploadUrl || !result.url) {
        const errorMessage = result.error || 'Failed to create upload url';
        reportUploadErrorToSentry(
          buildReportableError(errorMessage, {
            status: presignResponse.status,
            response: result,
          }),
          {
            runtime: 'browser',
            stage: 'presign_response',
            endpoint: '/node-api/upload',
            fileName: fileToUpload.name,
            fileType: fileToUpload.type || 'application/octet-stream',
            fileSize: fileToUpload.size,
            nodeType: trackingNodeType,
            folder,
            status: presignResponse.status,
            method: 'POST',
            contentHash,
          }
        );
        pushDataLayerEvent('asset_upload_failed', buildUploadPayload(fileToUpload, trackingNodeType, {
          upload_method: 'POST',
          failure_stage: 'presign_response',
          error_code: String(presignResponse.status),
          provider: 'unknown',
          reason: errorMessage,
        }));
        if (!silent) {
          toast.error(`${t('infiniteCanvas.toast.uploadFailed')}: ${errorMessage}`, {
            ...(processingToastId != null ? { id: processingToastId } : {}),
            duration: Infinity,
            action: {
              label: t('common.confirm'),
              onClick: () => {},
            },
          });
        }
        options.onError?.(errorMessage);
        return null;
      }

      failureStage = 'direct_upload';
      uploadMethod = result.method || 'PUT';
      uploadUrl = result.uploadUrl;
      uploadProvider = getUploadProvider(result.uploadUrl);
      await uploadFileWithProgress(result.uploadUrl, result.method || 'PUT', fileToUpload, onProgress);
      pushDataLayerEvent('asset_upload_completed', buildUploadPayload(fileToUpload, trackingNodeType, {
        upload_method: uploadMethod,
        provider: uploadProvider,
      }));
      if (!silent) {
        toast.success(t('infiniteCanvas.toast.uploadSuccess'), processingToastId != null ? { id: processingToastId } : undefined);
      }
      options.onSuccess?.(result.url, fileToUpload.name, nodeType, dimensions, audioDurationSec, videoDurationSec, aspectRatioWarning, mediaHealth);
      return {
        url: result.url,
        fileName: fileToUpload.name,
        nodeType,
        dimensions,
        videoDurationSec,
        audioDurationSec,
        aspectRatioWarning,
        mediaHealth,
      };
    } catch (error) {
      const reportableError = error instanceof Error
        ? error
        : buildReportableError('Upload failed', { cause: error });
      reportUploadErrorToSentry(reportableError, {
        runtime: 'browser',
        stage: failureStage,
        endpoint: '/node-api/upload',
        fileName: fileToUpload.name,
        fileType: fileToUpload.type || 'application/octet-stream',
        fileSize: fileToUpload.size,
        nodeType: trackingNodeType,
        folder,
        method: uploadMethod,
        contentHash,
        uploadUrl,
        provider: uploadProvider,
        status: uploadStatus,
      });
      pushDataLayerEvent('asset_upload_failed', buildUploadPayload(fileToUpload, trackingNodeType, {
        upload_method: uploadMethod,
        failure_stage: failureStage,
        provider: uploadProvider ?? 'unknown',
        status: uploadStatus,
        error_code: reportableError.name || 'upload_failed',
        reason: reportableError.message,
      }));
      if (!silent) {
        toast.error(t('infiniteCanvas.toast.uploadFailed'), {
          ...(processingToastId != null ? { id: processingToastId } : {}),
          duration: Infinity,
          action: {
            label: t('common.confirm'),
            onClick: () => {},
          },
        });
      }
      options.onError?.(reportableError.message);
      return null;
    } finally {
      setUploading(false);
    }
  }, [options, t]);

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    await uploadFile(file);
    
    e.target.value = '';
  }, [uploadFile]);

  return {
    uploading,
    uploadFile,
    handleFileChange,
  };
}
